import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = ({ onAddToCart }) => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Erreur lors du chargement du produit');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-product">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <p className="text-muted-foreground">Produit non trouvé</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Link
          to="/produits"
          className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-8"
          data-testid="back-to-products-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux produits
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square rounded-xl overflow-hidden bg-muted" data-testid="product-detail-image">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="lg:sticky lg:top-24 h-fit">
            <div className="mb-4">
              <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground" data-testid="product-detail-category">
                {product.category}
              </span>
            </div>

            <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight mb-4" data-testid="product-detail-name">
              {product.name}
            </h1>

            <p className="text-base leading-relaxed text-muted-foreground mb-6" data-testid="product-detail-description">
              {product.description}
            </p>

            <div className="flex items-baseline gap-4 mb-8">
              <span className="text-5xl font-bold text-primary" data-testid="product-detail-price">
                {product.price.toFixed(2)} DH
              </span>
            </div>

            {product.specs && Object.keys(product.specs).length > 0 && (
              <div className="bg-white border border-border rounded-xl p-6 mb-8" data-testid="product-specs">
                <h3 className="font-heading font-medium text-xl mb-4">Caractéristiques Techniques</h3>
                <dl className="space-y-3">
                  {Object.entries(product.specs).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <dt className="text-muted-foreground">{key}</dt>
                      <dd className="font-medium">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <label className="font-medium">Quantité :</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-input hover:bg-muted transition-colors"
                  data-testid="decrease-quantity-btn"
                >
                  -
                </button>
                <span className="w-12 text-center font-medium" data-testid="quantity-display">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border border-input hover:bg-muted transition-colors"
                  data-testid="increase-quantity-btn"
                >
                  +
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                for (let i = 0; i < quantity; i++) {
                  onAddToCart(product);
                }
                toast.success(`${quantity} ${quantity > 1 ? 'produits ajoutés' : 'produit ajouté'} au panier`);
              }}
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-14 px-8 rounded-full font-medium transition-all hover:scale-105 active:scale-95 shadow-lg shadow-accent/20 flex items-center justify-center gap-2"
              data-testid="add-to-cart-detail-btn"
            >
              <ShoppingCart className="w-5 h-5" />
              Ajouter au panier
            </button>

            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Livraison gratuite dès 500 DH</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Garantie 2 ans</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>Installation disponible</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};